import {
  DialogActionTrigger,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { useAcknowledgeDownloadSession } from '@/hooks/actions/use-acknowledge-download-session';
import { Box, Button, Text, VStack } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface DownloadAcknowledgmentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onAcknowledged: () => void;
}

export const DownloadAcknowledgmentDialog: React.FC<DownloadAcknowledgmentDialogProps> = ({
  isOpen,
  onClose,
  onAcknowledged,
}) => {
  const { t } = useTranslation();
  const ackMutation = useAcknowledgeDownloadSession();

  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (!isOpen) setConfirmed(false);
  }, [isOpen]);

  const isPending = ackMutation.isPending;
  const canConfirm = confirmed && !isPending;

  const handleOpenChange = ({ open }: { open: boolean }) => {
    if (!open && !isPending) onClose();
  };

  const handleConfirm = async () => {
    if (!canConfirm) return;
    try {
      await ackMutation.mutateAsync();
      onAcknowledged();
    } catch {
      // Keep the dialog open on error so the user can retry.
    }
  };

  return (
    <DialogRoot
      open={isOpen}
      onOpenChange={handleOpenChange}
      placement="center"
      closeOnInteractOutside={false}
      closeOnEscape={!isPending}
    >
      <DialogContent rounded="2xl" maxW="md">
        <DialogHeader>
          <DialogTitle>{t('mentions.download.acknowledgment.title')}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <VStack align="stretch" gap={4}>
            <Text fontSize="sm" color="gray.700">
              {t('mentions.download.acknowledgment.subtitle')}
            </Text>

            <Checkbox
              checked={confirmed}
              onCheckedChange={(e) => setConfirmed(Boolean(e.checked))}
              alignItems="flex-start"
              data-testid="download-ack-checkbox"
            >
              <Box fontSize="sm" lineHeight="snug">
                {t('mentions.download.acknowledgment.checkbox')}
              </Box>
            </Checkbox>
          </VStack>
        </DialogBody>
        <DialogFooter>
          <DialogActionTrigger asChild>
            <Button variant="outline" onClick={onClose} disabled={isPending}>
              {t('mentions.download.acknowledgment.cta_cancel')}
            </Button>
          </DialogActionTrigger>
          <Button
            colorPalette="gray"
            onClick={handleConfirm}
            disabled={!canConfirm}
            loading={isPending}
            data-testid="download-ack-confirm"
          >
            {t('mentions.download.acknowledgment.cta_confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};
